import {
  Archive,
  ArrowLeftRight,
  BadgeCheck,
  Banknote,
  Bird,
  BookOpen,
  Box,
  Boxes,
  Building,
  Building2,
  Calculator,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  ChartColumn,
  Check,
  ClipboardList,
  CircleCheck,
  CirclePause,
  CircleX,
  Clock,
  Clock3,
  Coins,
  Columns3,
  Copy,
  Download,
  Eraser,
  Fence,
  FilePen,
  FileText,
  Fuel,
  Gauge,
  GripHorizontal,
  HandCoins,
  Handshake,
  History,
  House,
  IdCard,
  Info,
  Landmark,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  Lock,
  MapPin,
  Megaphone,
  Network,
  Package,
  PackageMinus,
  PackagePlus,
  Pencil,
  Percent,
  Pill,
  Plus,
  Printer,
  ReceiptText,
  RotateCcw,
  Ruler,
  Send,
  Settings2,
  ShoppingBasket,
  Shuffle,
  SlidersHorizontal,
  Smartphone,
  Syringe,
  Tag,
  Tags,
  Tractor,
  Trash2,
  TriangleAlert,
  Truck,
  Undo2,
  Upload,
  UserMinus,
  UserPlus,
  UserRound,
  UserX,
  Users,
  Wallet,
  Warehouse,
  Wheat,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * The one place the app picks its icons. Screens name a kind, never a Lucide
 * icon, so changing what something looks like is a one-line edit here.
 */
const ICONS = {
  // Farm Manager places
  allFarms: Tractor,
  farm: MapPin,
  shed: House,
  section: LayoutGrid,
  pen: Fence,
  store: Warehouse,
  flock: Bird,
  dashboard: LayoutDashboard,
  capacity: Gauge,
  attention: TriangleAlert,
  announcement: Megaphone,

  // Inventory areas
  stock: Boxes,
  catalog: BookOpen,
  inbound: PackagePlus,
  outbound: PackageMinus,
  transfer: ArrowLeftRight,
  returnGoods: Undo2,
  adjust: SlidersHorizontal,
  demand: ClipboardList,
  centralStore: Building2,
  lots: Layers,
  bin: Box,
  value: Wallet,
  item: Package,
  expiring: CalendarClock,
  category: Tags,
  unit: Ruler,
  supplier: Truck,

  // Item categories, by their built-in codes (see categoryKind)
  catFeed: Wheat,
  catMedicine: Pill,
  catVaccine: Syringe,
  catFuel: Fuel,
  catConsumables: ShoppingBasket,
  catOther: Tag,

  // Detail tabs
  stockCard: FileText,
  details: Info,
  history: History,

  // Actions
  add: Plus,
  edit: Pencil,
  post: CircleCheck,
  repeat: Copy,
  copy: Copy,
  print: Printer,
  approve: Check,
  reject: X,
  reverse: Undo2,
  remove: Trash2,
  export: Download,
  retire: Archive,
  reactivate: RotateCcw,
  columns: Columns3,
  clear: Eraser,

  // HR & Payroll
  employee: UserRound,
  people: Users,
  department: Network,
  designation: IdCard,
  shift: Clock3,
  attendance: CalendarCheck,
  month: CalendarDays,
  leave: CalendarOff,
  advance: HandCoins,
  loan: Landmark,
  payroll: Banknote,
  payslip: ReceiptText,
  settlement: Handshake,
  cash: Coins,
  bank: Building,
  wallet: Smartphone,
  report: ChartColumn,
  setup: Settings2,
  tax: Percent,
  importFile: Upload,
  moveFarm: Shuffle,
  joiner: UserPlus,
  leaver: UserMinus,
  suspend: UserX,
  calculate: Calculator,
  submit: Send,
  locked: Lock,
  hold: CirclePause,
  paid: BadgeCheck,
  checklist: ListChecks,
  drag: GripHorizontal,

  // Document statuses
  statusDraft: FilePen,
  statusPending: Clock,
  statusDone: CircleCheck,
  statusRefused: CircleX,
  statusReversed: Undo2,
} satisfies Record<string, LucideIcon>;

export type IconKind = keyof typeof ICONS;

/**
 * The icon for an item category. The five built-in categories get their own;
 * any category someone creates later gets a plain tag, since there is no way
 * to know what it holds.
 */
export function categoryKind(code: string | null | undefined): IconKind {
  switch ((code ?? '').toUpperCase()) {
    case 'FEED':
      return 'catFeed';
    case 'MED':
      return 'catMedicine';
    case 'VAC':
      return 'catVaccine';
    case 'FUEL':
      return 'catFuel';
    case 'CONS':
      return 'catConsumables';
    default:
      return 'catOther';
  }
}

/** The icon for a document or approval status, e.g. "POSTED". */
export function statusKind(status: string): IconKind {
  switch (status.toUpperCase()) {
    case 'DRAFT':
      return 'statusDraft';
    case 'PENDING':
    case 'NOT_SENT':
      return 'statusPending';
    case 'POSTED':
    case 'APPROVED':
    case 'FULFILLED':
    case 'SENT':
      return 'statusDone';
    case 'REJECTED':
    case 'CANCELLED':
    case 'REFUSED':
      return 'statusRefused';
    case 'REVERSED':
      return 'statusReversed';
    default:
      return 'details';
  }
}

/**
 * A decorative icon. Its colour comes from the `appicon--<kind>` class, and it
 * is always shown beside a text label — never on its own — so nothing depends
 * on seeing the colour or the shape.
 */
export function AppIcon({ kind, size = 16 }: { kind: IconKind; size?: number }) {
  const Icon = ICONS[kind];
  return (
    <Icon
      className={`appicon appicon--${kind}`}
      size={size}
      // Matches the hand-drawn sidebar icons' line weight.
      strokeWidth={1.6}
      aria-hidden="true"
      focusable="false"
    />
  );
}
